import React, { useState } from 'react';
import { Plus, Pencil, Trash2, X, RefreshCw, Copy, Check, KeyRound, ShieldCheck } from 'lucide-react';
import api from '../../api';

const inputClass = "px-3.5 py-2.5 border border-gray-300 rounded-lg text-[15px] transition-all bg-white focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-600/10";
const primaryBtn = "bg-blue-600 text-white px-4 py-2.5 border-none rounded-lg text-sm font-medium cursor-pointer transition-colors hover:bg-blue-700";
const secondaryBtn = "bg-white text-gray-700 px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium cursor-pointer transition-all hover:bg-gray-50 hover:border-gray-400";

const formatMoment = (value) => (value ? new Date(value).toLocaleString('ru-RU', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
}) : null);

/** Поле с учётными данными: показывается один раз, поэтому рядом кнопка «скопировать». */
const SecretField = ({ label, value }) => {
    const [copied, setCopied] = useState(false);

    const copy = () => {
        navigator.clipboard.writeText(value).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <div className="mb-3">
            <div className="text-sm font-medium text-gray-700 mb-1">{label}</div>
            <div className="flex gap-2">
                <code className="flex-1 px-3 py-2 bg-gray-100 rounded-lg text-[13px] break-all font-mono text-gray-900">
                    {value}
                </code>
                <button className={secondaryBtn + ' shrink-0 flex items-center gap-1.5'} onClick={copy}>
                    {copied ? <Check size={14} /> : <Copy size={14} />}
                    {copied ? 'Готово' : 'Копировать'}
                </button>
            </div>
        </div>
    );
};

/**
 * Почтовые клиенты - учётные записи сервисов, которым разрешено отправлять письма.
 *
 * <p>Отдельно от OAuth2-клиентов: сервису, который шлёт письма, не нужно право входить за
 * пользователей, а шлюзу не нужно право рассылать почту.
 */
const MailClientsSection = ({ clients, senders, onChanged, onAlert }) => {
    const [form, setForm] = useState(null);
    const [editingId, setEditingId] = useState(null);
    const [credentials, setCredentials] = useState(null);
    const [showGuide, setShowGuide] = useState(false);

    const errorText = (err, fallback) =>
        err?.response?.data?.message || (typeof err?.response?.data === 'string' && err.response.data) || fallback;

    const openCreate = () => {
        setEditingId(null);
        setForm({ name: '', senderIds: [], ratePerMinute: '', enabled: true });
    };

    const openEdit = (client) => {
        setEditingId(client.id);
        setForm({
            name: client.name,
            senderIds: client.senderIds || [],
            ratePerMinute: client.ratePerMinute ?? '',
            enabled: client.enabled,
        });
    };

    const toggleSender = (id) => {
        setForm(f => ({
            ...f,
            senderIds: f.senderIds.includes(id) ? f.senderIds.filter(x => x !== id) : [...f.senderIds, id],
        }));
    };

    const handleSave = () => {
        const payload = {
            name: form.name,
            senderIds: form.senderIds,
            ratePerMinute: form.ratePerMinute === '' ? null : Number(form.ratePerMinute),
            enabled: form.enabled,
        };

        if (editingId) {
            api.put(`/api/admin/mail/clients/${editingId}`, payload)
                .then(() => {
                    setForm(null);
                    onChanged();
                    onAlert('success', 'Клиент сохранён');
                })
                .catch(err => onAlert('error', errorText(err, 'Не удалось сохранить клиента')));
        } else {
            api.post('/api/admin/mail/clients', payload)
                .then(res => {
                    setForm(null);
                    setCredentials(res.data);
                    onChanged();
                })
                .catch(err => onAlert('error', errorText(err, 'Не удалось создать клиента')));
        }
    };

    const handleRegenerate = (client) => {
        if (!window.confirm(
            `Перевыпустить доступы для «${client.name}»? Прежний секрет и ключ подписи перестанут работать сразу, вместе с уже выданными токенами.`)) return;

        api.post(`/api/admin/mail/clients/${client.id}/regenerate`)
            .then(res => {
                setCredentials(res.data);
                onChanged();
            })
            .catch(err => onAlert('error', errorText(err, 'Не удалось перевыпустить доступы')));
    };

    const handleDelete = (client) => {
        if (!window.confirm(`Удалить клиента «${client.name}»? Отправлять письма через API он больше не сможет.`)) return;
        api.delete(`/api/admin/mail/clients/${client.id}`)
            .then(() => {
                onChanged();
                onAlert('success', 'Клиент удалён');
            })
            .catch(err => onAlert('error', errorText(err, 'Не удалось удалить клиента')));
    };

    const senderName = (id) => senders.find(s => s.id === id)?.fromEmail || `отправитель ${id}`;

    return (
        <div className="bg-white rounded-xl p-6 mb-6 shadow-sm border border-gray-200">
            <div className="flex items-start justify-between mb-1">
                <h3 className="m-0 text-lg font-semibold text-gray-900">Клиенты API</h3>
                <button className={primaryBtn + ' flex items-center gap-1.5'} onClick={openCreate}>
                    <Plus size={16} /> Добавить
                </button>
            </div>
            <p className="text-gray-500 text-sm mb-5">
                Учётные записи сервисов, которым разрешено отправлять письма через API. Отдельные
                от OAuth2-клиентов: сервису, который шлёт письма, не нужно право входить за
                пользователей, а шлюзу не нужно право рассылать почту.
            </p>

            {clients.length === 0 ? (
                <p className="text-sm text-gray-500 m-0 mb-5">Клиентов пока нет.</p>
            ) : (
                <ul className="list-none p-0 m-0 mb-5">
                    {clients.map(client => (
                        <li key={client.id} className="flex items-start justify-between gap-4 py-4 border-b border-gray-100 last:border-b-0">
                            <div className="min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-medium text-gray-900">{client.name}</span>
                                    <code className="text-[12px] font-mono text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                                        {client.clientId}
                                    </code>
                                    {!client.enabled && (
                                        <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-gray-100 text-gray-600 border border-gray-200">
                                            выключен
                                        </span>
                                    )}
                                </div>
                                <div className="text-[13px] text-gray-500 mt-1">
                                    {client.senderIds?.length
                                        ? `адреса: ${client.senderIds.map(senderName).join(', ')}`
                                        : 'адреса не назначены — отправлять не с чего'}
                                </div>
                                <div className="text-xs text-gray-400 mt-1">
                                    {client.ratePerMinute ? `предел: ${client.ratePerMinute} писем/мин` : 'предел общий'}
                                    {client.lastUsedAt
                                        ? ` · последний токен ${formatMoment(client.lastUsedAt)}`
                                        : ' · токен ещё не брали'}
                                </div>
                            </div>
                            <div className="flex gap-2 shrink-0">
                                <button className={secondaryBtn + ' flex items-center gap-1.5'} title="Перевыпустить доступы"
                                        onClick={() => handleRegenerate(client)}>
                                    <RefreshCw size={14} /> Доступы
                                </button>
                                <button className={secondaryBtn + ' flex items-center gap-1.5'} onClick={() => openEdit(client)}>
                                    <Pencil size={14} /> Изменить
                                </button>
                                <button
                                    className="bg-transparent text-gray-400 p-2 border border-transparent rounded-md cursor-pointer transition-all hover:text-red-600 hover:border-red-200 hover:bg-red-50"
                                    title="Удалить клиента"
                                    onClick={() => handleDelete(client)}
                                >
                                    <Trash2 size={16} />
                                </button>
                            </div>
                        </li>
                    ))}
                </ul>
            )}

            <button
                type="button"
                className="bg-transparent border-none text-blue-600 text-sm font-medium cursor-pointer p-0 hover:underline"
                onClick={() => setShowGuide(v => !v)}
            >
                {showGuide ? 'Скрыть' : 'Как сервису отправлять письма'}
            </button>

            {showGuide && (
                <div className="mt-4 p-4 bg-gray-50 border border-gray-200 rounded-lg text-[13px] text-gray-700 leading-relaxed">
                    <p className="mt-0 mb-3">
                        Два шага: получить токен, затем отправлять письма, подписывая тело запроса.
                        Токен живёт 15 минут, подпись действительна 5 минут — старый перехваченный
                        запрос повторить не выйдет.
                    </p>
                    <pre className="bg-white border border-gray-200 rounded-md p-3 overflow-x-auto text-[12px] font-mono m-0 mb-3">
{`# 1. Токен
POST /api/mail/v1/token
{"clientId": "mail-xxxx", "clientSecret": "..."}

# 2. Письмо
POST /api/mail/v1/send
Authorization: Bearer <токен>
X-Burger-Signature: t=<unix>,v1=<hex>
Idempotency-Key: <любой уникальный ключ>

{"senderId": 1, "to": "user@example.com",
 "subject": "Тема", "html": "<html>…</html>"}`}
                    </pre>
                    <p className="m-0">
                        Подпись: <code className="font-mono">HMAC_SHA256(ключ_подписи, "&lt;t&gt;." + тело)</code> в
                        шестнадцатеричном виде — тот же формат, что у исходящих вебхуков сервиса.
                        Вместо <code className="font-mono">html</code> можно передать{' '}
                        <code className="font-mono">templateId</code> и <code className="font-mono">data</code>,
                        чтобы письмо собралось по нашему шаблону.
                    </p>
                </div>
            )}

            {form && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setForm(null)}>
                    <div className="bg-white rounded-xl p-6 w-full max-w-[520px] max-h-[90vh] overflow-y-auto shadow-xl" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-5">
                            <h3 className="m-0 text-lg font-semibold text-gray-900">
                                {editingId ? 'Клиент API' : 'Новый клиент API'}
                            </h3>
                            <button className="bg-transparent border-none cursor-pointer text-gray-400 hover:text-gray-600 p-1" onClick={() => setForm(null)}>
                                <X size={20} />
                            </button>
                        </div>

                        <div className="mb-4 flex flex-col gap-1.5">
                            <label className="text-sm font-medium text-gray-700">Название сервиса</label>
                            <input className={inputClass} value={form.name} placeholder="Конструктор"
                                   onChange={e => setForm({ ...form, name: e.target.value })} />
                        </div>

                        <div className="mb-4 flex flex-col gap-1.5">
                            <label className="text-sm font-medium text-gray-700">Разрешённые адреса</label>
                            <p className="text-[13px] text-gray-500 m-0 mb-1">
                                С этих адресов клиент сможет отправлять письма. Ничего не выбрано — отправлять не с чего.
                            </p>
                            <div className="flex flex-col gap-2">
                                {senders.filter(s => s.enabled).map(sender => (
                                    <label key={sender.id} className="flex items-center gap-2.5 text-sm text-gray-700 cursor-pointer">
                                        <input type="checkbox" checked={form.senderIds.includes(sender.id)}
                                               onChange={() => toggleSender(sender.id)} />
                                        {sender.name} <span className="text-gray-400">({sender.fromEmail})</span>
                                    </label>
                                ))}
                                {senders.filter(s => s.enabled).length === 0 && (
                                    <span className="text-[13px] text-gray-500">Сначала заведите отправителя.</span>
                                )}
                            </div>
                        </div>

                        <div className="mb-4 flex flex-col gap-1.5">
                            <label className="text-sm font-medium text-gray-700">Предел, писем в минуту</label>
                            <input className={inputClass} type="number" min="1" value={form.ratePerMinute}
                                   placeholder="по умолчанию — общий предел сервиса"
                                   onChange={e => setForm({ ...form, ratePerMinute: e.target.value })} />
                        </div>

                        <label className="flex items-center gap-2.5 text-sm text-gray-700 cursor-pointer mb-5">
                            <input type="checkbox" checked={form.enabled}
                                   onChange={e => setForm({ ...form, enabled: e.target.checked })} />
                            Включён
                        </label>

                        <div className="flex gap-3">
                            <button className={primaryBtn} onClick={handleSave}>Сохранить</button>
                            <button className={secondaryBtn} onClick={() => setForm(null)}>Отмена</button>
                        </div>
                    </div>
                </div>
            )}

            {credentials && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl p-6 w-full max-w-[560px] shadow-xl">
                        <h3 className="m-0 mb-2 text-lg font-semibold text-gray-900 flex items-center gap-2">
                            <ShieldCheck size={20} className="text-emerald-600" /> Доступы выданы
                        </h3>
                        <p className="text-sm text-gray-600 mb-5">
                            Секрет и ключ подписи показываются один раз — потом их взять негде: секрет
                            хранится хэшем, а ключ обратно не отдаётся. Перенесите их в настройки сервиса сейчас.
                        </p>

                        <SecretField label="clientId" value={credentials.clientId} />
                        <SecretField label="clientSecret" value={credentials.clientSecret} />
                        <SecretField label="Ключ подписи" value={credentials.signingKey} />

                        <div className="flex items-start gap-2.5 bg-amber-50 border border-amber-200 text-amber-900 rounded-lg p-3 text-[13px] mb-5">
                            <KeyRound size={18} className="shrink-0 mt-0.5" />
                            <span>
                                Ключ подписи нужен для заголовка <code className="font-mono">X-Burger-Signature</code>.
                                Без него запросы будут отвергаться, даже с верным токеном.
                            </span>
                        </div>

                        <button className={primaryBtn} onClick={() => setCredentials(null)}>
                            Я сохранил
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MailClientsSection;
